/**
 * API 服务封装
 * 处理所有与后端的通信
 */
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// 创建 axios 实例
const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 300000, // 5分钟超时（打标任务可能很长）
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：添加认证 Token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    // 如果是 401 错误，清除 token（保持在当前页面，用户会看到未登录状态）
    if (error.response?.status === 401) {
      console.log('Token 已过期或无效，已清空登录状态');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      // 不跳转，让页面自然显示未登录状态，用户需要时会自己登录
    }
    return Promise.reject(error);
  }
);

/**
 * 认证相关 API
 */

/**
 * Google OAuth 登录
 */
export const login = async (googleIdToken) => {
  const response = await apiClient.post('/api/auth/login', {
    google_id_token: googleIdToken,
  });
  return response.data;
};

/**
 * 登出
 */
export const logout = async () => {
  const response = await apiClient.post('/api/auth/logout');
  return response.data;
};

/**
 * 获取当前用户信息
 */
export const getCurrentUser = async () => {
  const response = await apiClient.get('/api/auth/me');
  return response.data;
};

/**
 * 获取可用模型列表（v2.6新增）
 * @param {string} dataType - 数据类型过滤（TEXT/IMAGE/VIDEO），可选
 */
export const getAvailableModels = async (dataType = null) => {
  const params = dataType ? { data_type: dataType } : {};
  const response = await apiClient.get('/api/models', { params });
  return response.data;
};

/**
 * 升级为内测用户
 */
export const upgradeToTester = async (accessCode) => {
  const response = await apiClient.post('/api/auth/upgrade', {
    access_code: accessCode,
  });
  return response.data;
};

/**
 * 上传文件和密钥
 */
export const uploadFiles = async (excelFile, keyFile = null) => {
  const formData = new FormData();
  formData.append('file', excelFile);
  if (keyFile) {
    formData.append('key_file', keyFile);
  }

  const response = await apiClient.post('/api/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * 启动处理任务（旧版，兼容性保留）
 */
export const startProcessing = async (taskId, userRequest) => {
  const response = await apiClient.post('/api/process', {
    task_id: taskId,
    user_request: userRequest,
  });

  return response.data;
};

/**
 * 获取Excel列名（v2.0新增）
 */
export const getExcelColumns = async (taskId) => {
  const response = await apiClient.get(`/api/excel/columns/${taskId}`);
  return response.data;
};

/**
 * 获取Excel列名及类型检测（v3.0新增）
 * 自动检测列类型：image/video/object/text
 */
export const getExcelColumnsWithTypes = async (taskId) => {
  const response = await apiClient.get(`/api/excel/columns-with-types/${taskId}`);
  return response.data;
};

/**
 * 启动结构化打标任务（v2.0新增，v2.6支持model_id，v2.8支持omni_config，v3.0支持flexible_config）
 */
export const startStructuredProcessing = async (config) => {
  console.log('🌐 [API] startStructuredProcessing 收到配置:', config);

  const requestData = {
    task_id: config.taskId,
    target_column: config.targetColumn,
    data_type: config.dataType,
    prompt_instructions: config.promptInstructions,
    // v2.5新增：成本信息
    estimated_cost: config.estimated_cost,
    estimated_tokens: config.estimated_tokens,
    // v2.6新增：模型选择
    model_id: config.model_id,
    // v2.8新增：OMNI配置
    omni_config: config.omni_config,
    // v3.0新增：FLEXIBLE配置
    flexible_config: config.flexible_config,
    // v3.1新增：层级打标配置
    hierarchy_enabled: config.hierarchy_enabled,
    hierarchy_prompt: config.hierarchy_prompt,
  };

  console.log('📡 [API] 准备发送 POST /api/process/v2');
  console.log('📡 [API] 请求数据:', requestData);
  console.log('📡 [API] 请求数据 JSON:', JSON.stringify(requestData, null, 2));

  const response = await apiClient.post('/api/process/v2', requestData);

  console.log('📨 [API] 收到响应:', response.data);

  return response.data;
};

/**
 * 试打前N行数据（v2.1新增，v2.6支持model_id，v2.8支持omni_config，v3.0支持flexible_config，v3.1支持hierarchy）
 */
export const submitTrialRequest = async (config) => {
  console.log('🧪 [API] submitTrialRequest 收到配置:', config);

  const requestData = {
    task_id: config.taskId,
    target_column: config.targetColumn,
    data_type: config.dataType,
    prompt_instructions: config.promptInstructions,
    max_rows: config.maxRows || 20,
    // v2.6新增：模型选择
    model_id: config.model_id,
    // v2.8新增：OMNI配置
    omni_config: config.omni_config,
    // v3.0新增：FLEXIBLE配置
    flexible_config: config.flexible_config,
    // v3.1新增：层级打标配置
    hierarchy_enabled: config.hierarchy_enabled,
    hierarchy_prompt: config.hierarchy_prompt,
  };

  console.log('📡 [API] 准备发送 POST /api/trial');
  console.log('📡 [API] 请求数据:', requestData);

  const response = await apiClient.post('/api/trial', requestData);

  console.log('📨 [API] 试打响应:', response.data);

  return response.data;
};

/**
 * 预估任务成本（v2.5新增，v2.6支持model_id，v2.8支持omni_config，v3.0支持flexible_config）
 */
export const estimateCost = async (config) => {
  console.log('💰 [API] estimateCost 收到配置:', config);

  const requestData = {
    task_id: config.taskId,
    target_column: config.targetColumn,
    data_type: config.dataType,
    prompt_instructions: config.promptInstructions,
    // v2.6新增：模型选择
    model_id: config.model_id,
    // v2.8新增：OMNI配置
    omni_config: config.omni_config,
    // v3.0新增：FLEXIBLE配置
    flexible_config: config.flexible_config,
  };

  console.log('📡 [API] 准备发送 POST /api/estimate');
  console.log('📡 [API] 请求数据:', requestData);

  const response = await apiClient.post('/api/estimate', requestData);

  console.log('📨 [API] 成本预估响应:', response.data);

  return response.data;
};

/**
 * 查询任务状态
 */
export const getTaskStatus = async (taskId) => {
  const response = await apiClient.get(`/api/status/${taskId}`);
  return response.data;
};

/**
 * 获取下载链接（已弃用，保留以兼容旧代码）
 */
export const getDownloadUrl = (taskId) => {
  return `${API_URL}/api/download/${taskId}`;
};

/**
 * 下载文件（v2.3：带认证token）
 */
export const downloadFile = async (taskId, filename) => {
  try {
    const response = await apiClient.get(`/api/download/${taskId}`, {
      responseType: 'blob', // 重要：告诉 axios 返回二进制数据
    });

    // 创建下载链接
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename); // 设置文件名
    document.body.appendChild(link);
    link.click();

    // 清理
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('下载失败:', error);
    throw error;
  }
};

/**
 * 获取任务列表
 */
export const getTaskList = async (limit = 20) => {
  const response = await apiClient.get('/api/tasks', {
    params: { limit },
  });
  return response.data;
};

/**
 * 健康检查
 */
export const healthCheck = async () => {
  const response = await apiClient.get('/api/health');
  return response.data;
};

/**
 * Prompt模板相关 API
 */

/**
 * 获取当前用户的所有模板
 */
export const getTemplates = async () => {
  const response = await apiClient.get('/api/templates');
  return response.data;
};

/**
 * 创建新模板
 */
export const createTemplate = async (data) => {
  const response = await apiClient.post('/api/templates', data);
  return response.data;
};

/**
 * 更新模板
 */
export const updateTemplate = async (id, data) => {
  const response = await apiClient.put(`/api/templates/${id}`, data);
  return response.data;
};

/**
 * 删除模板
 */
export const deleteTemplate = async (id) => {
  const response = await apiClient.delete(`/api/templates/${id}`);
  return response.data;
};

/**
 * 预览Excel数据
 */
export const getExcelPreview = async (taskId, filterColumn = null, filterValues = null, maxRows = 500) => {
  const params = { max_rows: maxRows };
  if (filterColumn) params.filter_column = filterColumn;
  if (filterValues && filterValues.length > 0) {
    params.filter_values = filterValues.join(',');
  }
  
  const response = await apiClient.get(`/api/excel/preview/${taskId}`, { params });
  return response.data;
};

export default apiClient;
